import { useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { ColorSwatchPicker } from '../ui/ColorSwatchPicker';
import { Textarea } from '../ui/textarea';
import {
  useCreateTaskTemplate,
  useDeleteTaskTemplate,
  useTaskTemplates,
  useUpdateTaskTemplate,
} from '../../hooks/queries/useTaskTemplates';
import { useTaskTags } from '../../hooks/queries/useTaskTags';
import { PRIORITY_META } from '../tasks/taskHelpers';
import type { TaskTemplate } from '../../services/tasks/taskTemplateService';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { TaskFieldFlagsSection } from './TaskFieldFlagsSection';
import { TaskQuickCaptureSection } from './TaskQuickCaptureSection';
import { TaskViewFlagsSection } from './TaskViewFlagsSection';
import { TaskHoverPreviewSection } from './TaskHoverPreviewSection';
import { TagsTab } from './TagsTab';
import { TocSection } from './SettingsToc';
import { PermissionGuard } from '../auth/PermissionGuard';

const PRIORITIES = [1, 2, 3, 4] as const;

type FormState = {
  content: string;
  description: string;
  priority: number;
  tagIds: string[];
  duration: string;
};

const EMPTY: FormState = {
  content: '',
  description: '',
  priority: 3,
  tagIds: [],
  duration: '',
};

function parseDuration(raw: string): number | null {
  const n = Number.parseInt(raw.trim(), 10);
  if (!raw.trim() || Number.isNaN(n) || n <= 0) return null;
  return n;
}

function TagPicker({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (id: string) => void;
}) {
  const { data: tags = [] } = useTaskTags();
  if (tags.length === 0) {
    return (
      <p className="text-xs text-cime-text-muted">
        Aucune étiquette. Créez-en dans la section Étiquettes ci-dessus.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => {
        const on = selected.includes(t.id);
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onToggle(t.id)}
            className={`text-xs px-2 py-0.5 rounded-full border transition ${
              on
                ? 'text-white border-transparent'
                : 'border-cime-border text-cime-text-muted hover:border-cime-primary'
            }`}
            style={on ? { backgroundColor: t.color } : undefined}
          >
            {t.name}
          </button>
        );
      })}
    </div>
  );
}

function PriorityPicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="flex gap-1">
      {PRIORITIES.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={`text-xs font-medium px-2 py-1 rounded border transition ${
            p === value
              ? PRIORITY_META[p]!.className
              : 'border-cime-border text-cime-text-muted hover:border-cime-primary'
          }`}
        >
          {PRIORITY_META[p]!.label}
        </button>
      ))}
    </div>
  );
}

function TemplateFields({
  form,
  setForm,
}: {
  form: FormState;
  setForm: (next: FormState) => void;
}) {
  const fields = useEffectiveTaskFields();
  function toggleTag(id: string) {
    setForm({
      ...form,
      tagIds: form.tagIds.includes(id)
        ? form.tagIds.filter((x) => x !== id)
        : [...form.tagIds, id],
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <Input
        value={form.content}
        onChange={(e) => setForm({ ...form, content: e.target.value })}
        placeholder="Titre de la tâche"
        maxLength={500}
      />
      <Textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Description (optionnelle)"
        rows={3}
        className="mt-0"
      />
      <div className="flex flex-wrap items-center gap-4">
        {fields.priority && (
        <div>
          <p className="text-xs text-cime-text-muted mb-1">Priorité</p>
          <PriorityPicker
            value={form.priority}
            onChange={(priority) => setForm({ ...form, priority })}
          />
        </div>
        )}
        <div>
          <p className="text-xs text-cime-text-muted mb-1">Durée estimée (min)</p>
          <Input
            type="number"
            min={1}
            value={form.duration}
            onChange={(e) => setForm({ ...form, duration: e.target.value })}
            placeholder="—"
            className="w-24"
          />
        </div>
      </div>
      {fields.tags && (
      <div>
        <p className="text-xs text-cime-text-muted mb-1">Étiquettes</p>
        <TagPicker selected={form.tagIds} onToggle={toggleTag} />
      </div>
      )}
    </div>
  );
}

function toForm(t: TaskTemplate): FormState {
  return {
    content: t.content,
    description: t.description ?? '',
    priority: t.priority,
    tagIds: t.tag_ids ?? [],
    duration: t.estimated_duration_minutes ? String(t.estimated_duration_minutes) : '',
  };
}

export function TaskTemplatesTab() {
  const fields = useEffectiveTaskFields();
  const { data: templates = [], isLoading, error } = useTaskTemplates(false);
  const { data: tags = [] } = useTaskTags();
  const create = useCreateTaskTemplate();
  const update = useUpdateTaskTemplate();
  const remove = useDeleteTaskTemplate();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = form.content.trim();
    if (!trimmed) return;
    try {
      await create.mutateAsync({
        content: trimmed,
        description: form.description.trim() || null,
        priority: form.priority,
        tag_ids: form.tagIds,
        estimated_duration_minutes: parseDuration(form.duration),
      });
      setForm(EMPTY);
      toast.success('Tâche type créée');
    } catch {
      toast.error('Impossible de créer la tâche type');
    }
  }

  async function saveEdit() {
    if (!editingId) return;
    const trimmed = editForm.content.trim();
    if (!trimmed) return;
    try {
      await update.mutateAsync({
        id: editingId,
        patch: {
          content: trimmed,
          description: editForm.description.trim() || null,
          priority: editForm.priority,
          tag_ids: editForm.tagIds,
          estimated_duration_minutes: parseDuration(editForm.duration),
        },
      });
      setEditingId(null);
      toast.success('Tâche type mise à jour');
    } catch {
      toast.error('Impossible de modifier la tâche type');
    }
  }

  async function handleDelete(id: string, title: string) {
    if (!window.confirm(`Supprimer la tâche type « ${title} » ?`)) return;
    try {
      await remove.mutateAsync(id);
      toast.success('Tâche type supprimée');
    } catch {
      toast.error('Suppression réservée aux administrateurs');
    }
  }

  const tagsById = new Map(tags.map((t) => [t.id, t]));

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-cime-text-primary">Tâches</h1>
        <p className="text-sm text-cime-text-muted mt-1">
          Réglages d’affichage, des champs, étiquettes et catalogue des tâches types.
        </p>
      </div>

      <TocSection label="Vues"><TaskViewFlagsSection /></TocSection>
      <TocSection label="Champs"><TaskFieldFlagsSection /></TocSection>
      <TocSection label="Saisie rapide"><TaskQuickCaptureSection /></TocSection>
      <TocSection label="Aperçu au survol"><TaskHoverPreviewSection /></TocSection>

      {fields.tags ? (
        <PermissionGuard feature="settings_tags" fallback={null}>
          <TocSection label="Étiquettes"><TagsTab /></TocSection>
        </PermissionGuard>
      ) : null}

      <TocSection label="Tâches types" className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-cime-text-primary">Tâches types</h2>
        <p className="text-sm text-cime-text-muted mt-1">
          Modèles réutilisables à la création d’une tâche (titre, description, priorité, étiquettes,
          durée). Pas de dossier ni d’échéance — ça se choisit au moment de créer. La pastille
          donne une couleur aux tâches créées depuis le modèle, dans l’agenda et les listes.
        </p>
      </div>

      <form
        onSubmit={handleCreate}
        className="flex flex-col gap-3 p-4 border border-neutral-200 rounded-cime bg-neutral-50"
      >
        <TemplateFields form={form} setForm={setForm} />
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={!form.content.trim() || create.isPending}>
            <Plus className="w-3.5 h-3.5" /> Créer la tâche type
          </Button>
        </div>
      </form>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Erreur de chargement des tâches types.</p>
      ) : templates.length === 0 ? (
        <p className="text-sm text-cime-text-muted italic">Aucune tâche type définie.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-cime">
          {templates.map((t) => (
            <li key={t.id} className="px-4 py-3">
              {editingId === t.id ? (
                <div className="flex flex-col gap-3">
                  <TemplateFields form={editForm} setForm={setEditForm} />
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={!editForm.content.trim() || update.isPending}
                      className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                      title="Enregistrer"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="p-1.5 text-neutral-400 hover:bg-neutral-100 rounded"
                      title="Annuler"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <ColorSwatchPicker
                    value={t.color}
                    label={`Couleur de « ${t.content} » dans l’agenda et les listes`}
                    onChange={(color) => update.mutate({ id: t.id, patch: { color } })}
                    onReset={t.color ? () => update.mutate({ id: t.id, patch: { color: null } }) : undefined}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-cime-text-primary">{t.content}</p>
                    {t.description && (
                      <p className="text-xs text-cime-text-muted mt-0.5 line-clamp-2">
                        {t.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {fields.priority && (
                      <span
                        className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${PRIORITY_META[t.priority]?.className ?? ''}`}
                      >
                        {PRIORITY_META[t.priority]?.label ?? `P${t.priority}`}
                      </span>
                      )}
                      {t.estimated_duration_minutes != null && (
                        <span className="text-[11px] text-cime-text-muted">
                          {t.estimated_duration_minutes} min
                        </span>
                      )}
                      {fields.tags && t.tag_ids.map((id) => {
                        const tag = tagsById.get(id);
                        if (!tag) return null;
                        return (
                          <span
                            key={id}
                            className="text-[11px] px-1.5 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: tag.color }}
                          >
                            {tag.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(t.id);
                      setEditForm(toForm(t));
                    }}
                    className="p-1.5 text-neutral-400 hover:text-cime-primary hover:bg-neutral-100 rounded"
                    title="Modifier"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(t.id, t.content)}
                    className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      </TocSection>
    </div>
  );
}
